import { call, cancelled, put } from 'redux-saga/effects';
import axios, { AxiosError, AxiosResponse } from 'axios';
import humps, { createHumps } from 'lodash-humps-ts';
import { snakeCase } from 'lodash';
import * as Sentry from '@sentry/react';

// import { logout } from '../auth/auth.saga-services';
import * as types from './api.types';
import * as actions from './api.actions';

import { select, selectWithParams } from '../../utils/selector.utils';
import { authSelectors } from '../auth/auth.selectors';
import { userSelectors } from '../resource/user/user.selectors';
import { addFlashMessage } from '../../utils/flashMessages.utils';
import { createServerErrorResponse, getApiFlashErrorMessage, getApiReduxError } from '../../utils/api.utils';

// SINGLETON, we don't need multiple instances of this
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  headers: {
    'Content-Type': 'application/json; charset=UTF-8',
    Accept: 'application/json',
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* apiRequest<TParams = any, TPayload = any, TResponse = any>(
  requestInfo: types.IApiRequestConfig<TParams, TPayload>,
) {
  const accessToken = yield select(authSelectors.selectAccessToken);
  const loggedUserId = yield select(authSelectors.selectLoggedUser);
  const loggedUser = loggedUserId ? yield selectWithParams(userSelectors.selectResourceById, loggedUserId) : null;
  const authHeaders: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };
  // JWT no longer includes tenant_id. The API resolves the workspace from X-Tenant-Id;
  // omitting it falls back to the user's first active membership, not the last workspace.
  // Prefer Redux; localStorage covers the first requests while the cached session hydrates.
  const tenantId = loggedUser?.tenantId ?? localStorage.getItem('last_tenant_selected');
  if (tenantId) {
    authHeaders['X-Tenant-Id'] = tenantId;
  }
  const cancelSource = yield call([axios.CancelToken, axios.CancelToken.source]);

  try {
    yield put(actions.storeApiInProgress(requestInfo.apiRouteKey));
    yield put(actions.clearApiError(requestInfo.apiRouteKey));

    const snakes = createHumps(snakeCase);
    let responseUnderscore: AxiosResponse;
    if (requestInfo.v2 === true) {
      const apiv2 = axios.create({
        baseURL: `${process.env.REACT_APP_API_URL_V2}${requestInfo.action}` || '',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          Accept: 'application/json',
        },
      });
      responseUnderscore = yield call([apiv2, apiv2.request], {
        method: requestInfo.method,
        params: {
          ...snakes(requestInfo.params),
        },
        data: {
          ...snakes(requestInfo.data),
        },
        cancelToken: cancelSource.token,
        responseType: requestInfo.responseType ? requestInfo.responseType : undefined,
        headers: authHeaders,
        ...requestInfo.overrides,
      });
    } else {
      responseUnderscore = yield call([api, api.request], {
        // url: `${requestInfo.uri}/`,
        url: requestInfo.uri,
        method: requestInfo.method,
        params: {
          action: requestInfo.action,
          ...snakes(requestInfo.params),
          rand: requestInfo.method === 'get' ? Math.floor(Math.random() * 100000) : undefined,
        },
        data: {
          ...snakes(requestInfo.data),
        },
        cancelToken: cancelSource.token,
        responseType: requestInfo.responseType ? requestInfo.responseType : undefined,
        headers: authHeaders,
        ...requestInfo.overrides,
      });
    }
    yield put(actions.clearApiInProgress(requestInfo.apiRouteKey));

    const response = humps(responseUnderscore) as AxiosResponse;
    if (response && response.data && response.data.status === 'fail') {
      yield put(actions.storeApiError(requestInfo.apiRouteKey, { message: response.data.message }));
      displayApiErrorFlashMessage(response);
      return null;
    } else if (response && response.data) {
      const result = response.data.result;
      if (result?.list) {
        return result.list as TResponse[];
      }
      return (result ?? null) as TResponse | null;
    }
    yield put(actions.storeApiError(requestInfo.apiRouteKey, { message: 'Something went wrong' }));
    displayApiErrorFlashMessage(response);
    return null;
  } catch (e) {
    // Not a real failure, just a cancellation or user navigates away from the page
    if (axios.isCancel(e)) {
      throw e;
    }

    // HTTP error with a response body - API returned a 400, 500, etc.
    const errorResponse = e as AxiosError;
    if (errorResponse.response) {
      yield put(actions.storeApiError(requestInfo.apiRouteKey, getApiReduxError(errorResponse.response)));
      yield put(actions.clearApiInProgress(requestInfo.apiRouteKey));

      displayApiErrorFlashMessage(errorResponse.response);
      return null;
    }

    // Axios error without a response - network, timeout, cors failures. The request never got a proper HTTP response
    if ((e as AxiosError).isAxiosError) {
      Sentry.captureException(e);
      const serverErrorResponse = createServerErrorResponse();
      yield put(actions.storeApiError(requestInfo.apiRouteKey, getApiReduxError(serverErrorResponse)));
      yield put(actions.clearApiInProgress(requestInfo.apiRouteKey));
      displayApiErrorFlashMessage(serverErrorResponse);
      return null;
    }

    throw e;
  } finally {
    if (yield cancelled()) {
      // cancel api call if task is cancelled
      yield call([cancelSource, cancelSource.cancel]);
    }
  }
}

function displayApiErrorFlashMessage(response: AxiosResponse<unknown>) {
  const { message, showDetails } = getApiFlashErrorMessage(response);
  addFlashMessage({
    type: 'error',
    message,
    statusCode: response.status,
    error: showDetails ? (response.data as types.IApiError) : undefined,
  });
}
